import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  Chip, Rating, Grid
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { getSharedWaxingEntries } from '../api';

export default function SharedWaxingEntriesSection({ team }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSharedWaxingEntries(team.id);
      setEntries(data);
      setError(null);
    } catch (err) {
      setError('Failed to load shared waxing entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'race': return 'error';
      case 'training': return 'primary';
      case 'test': return 'warning';
      default: return 'default';
    }
  };

  const getTechniqueColor = (technique) => {
    switch (technique) {
      case 'classic': return 'success';
      case 'skate': return 'info';
      case 'both': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Shared Waxing Entries
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {entries.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No shared waxing entries yet
          </Typography>
          <Typography color="text.secondary">
            Coaches in this team haven't shared any waxing diary entries yet.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {entries.map((entry) => (
            <Paper key={entry.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {entry.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(entry.date).toLocaleDateString()} • Shared by {entry.user_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Shared on {new Date(entry.shared_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={entry.activity_type}
                    color={getActivityTypeColor(entry.activity_type)}
                    size="small"
                  />
                  <Chip
                    label={entry.technique}
                    color={getTechniqueColor(entry.technique)}
                    size="small"
                  />
                </Box>
              </Box>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">View Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Location:</strong> {entry.location || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Temperature:</strong> {entry.temperature ? `${entry.temperature}°C` : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Humidity:</strong> {entry.humidity ? `${entry.humidity}%` : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Snow Conditions:</strong> {entry.snow_conditions || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Snow Type:</strong> {entry.snow_type || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Air Conditions:</strong> {entry.air_conditions || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Distance:</strong> {entry.distance ? `${entry.distance}km` : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Waxing</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Base Wax:</strong> {entry.base_wax || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Glide Wax:</strong> {entry.glide_wax || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Kick Wax:</strong> {entry.kick_wax || 'N/A'}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle2" gutterBottom>Performance</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={entry.performance_rating || 0} readOnly size="small" />
                        <Typography variant="body2">
                          ({entry.performance_rating || 0}/10)
                        </Typography>
                      </Box>
                    </Grid>
                    {entry.notes && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                        <Typography variant="body2">{entry.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
} 